import { Input, Checkbox, InputRef } from "antd";
import { useRef, useState } from "react";
import { AddAgentFormData } from "./types";
import InputMask from "react-input-mask";

interface BasicDetailsProps {
  formData: AddAgentFormData;
  existingEmails: string[];
  existingPhones: string[];
  onFormDataChange: (data: Partial<AddAgentFormData>) => void;
}

const BasicDetailsComponent: React.FC<BasicDetailsProps> = ({
  formData,
  existingEmails,
  existingPhones,
  onFormDataChange,
}) => {
  const { firstName, lastName, email, phone, hasAccess } = formData;

  const [errors, setErrors] = useState<Partial<AddAgentFormData>>({});

  const firstNameRef = useRef<InputRef>(null);
  const lastNameRef = useRef<InputRef>(null);
  const emailRef = useRef<InputRef>(null);
  const phoneRef = useRef<InputRef>(null);

  const isEmailValid = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const isPhoneValid = (phone: string) => {
    phone = formatPhoneNumber(phone);
    const regex = /^\d{10}$/;
    return regex.test(phone);
  };

  const formatPhoneNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, "").slice(0, 10);
    return cleaned;
  };

  const handleInputChange = (
    key: keyof AddAgentFormData,
    value: string | boolean
  ) => {
    const cleanedValue =
      key === "phone" ? formatPhoneNumber(value as string) : value;

    onFormDataChange({ [key]: cleanedValue });

    if (key === "email" && !isEmailValid(cleanedValue as string)) {
      setErrors({ ...errors, [key]: "Invalid email format" });
    } else if (
      key === "email" &&
      existingEmails.some(
        (email) => email.toLowerCase() === cleanedValue.toString().toLowerCase()
      )
    ) {
      setErrors({
        ...errors,
        [key]: "A user with the same email already exists",
      });
    } else if (key === "email") {
      setErrors({ ...errors, [key]: undefined });
    }

    if (key === "phone" && !isPhoneValid(cleanedValue as string)) {
      setErrors({ ...errors, [key]: "Invalid phone number" });
    } else if (key === "phone") {
      setErrors({ ...errors, [key]: undefined });
    }

    if (
      key === "firstName" &&
      (!cleanedValue ||
        typeof cleanedValue !== "string" ||
        cleanedValue.length < 1)
    ) {
      setErrors({ ...errors, [key]: "First name is required" });
    } else if (key === "firstName") {
      setErrors({ ...errors, [key]: undefined });
    }

    if (
      key === "lastName" &&
      (!cleanedValue ||
        typeof cleanedValue !== "string" ||
        cleanedValue.length < 1)
    ) {
      setErrors({ ...errors, [key]: "Last name is required" });
    } else if (key === "lastName") {
      setErrors({ ...errors, [key]: undefined });
    }
  };

  const handleKeyPress = (
    e: React.KeyboardEvent<HTMLInputElement>,
    nextRef: React.RefObject<InputRef>
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      nextRef.current?.focus();
    }
  };

  return (
    <div style={{ marginTop: "30px" }}>
      <div className="step-header">Agent Details</div>
      <div className="step-header-subtext">
        Enter info for the new agent below.
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        <div className={errors.firstName ? "field-error" : ""}>
          <label htmlFor="firstName">First Name</label>
          <Input
            id="firstName"
            ref={firstNameRef}
            autoComplete="new-password"
            placeholder="First Name"
            value={firstName}
            onChange={(e) => handleInputChange("firstName", e.target.value)}
            onKeyDown={(e) => handleKeyPress(e, lastNameRef)}
            tabIndex={100}
          />
        </div>
        <div className={errors.lastName ? "field-error" : ""}>
          <label htmlFor="lastName">Last Name</label>
          <Input
            id="lastName"
            ref={lastNameRef}
            autoComplete="new-password"
            placeholder="Last Name"
            value={lastName}
            onChange={(e) => handleInputChange("lastName", e.target.value)}
            onKeyDown={(e) => handleKeyPress(e, emailRef)}
            tabIndex={101}
          />
        </div>
        <div className={errors.email ? "field-error" : ""}>
          <label>Email</label>
          <Input
            ref={emailRef}
            autoComplete="new-password"
            placeholder="Email"
            value={email}
            onChange={(e) => handleInputChange("email", e.target.value)}
            onKeyDown={(e) => handleKeyPress(e, phoneRef)}
            tabIndex={102}
          />
          <div style={{ color: "#af6465", marginTop: "5px" }}>
            {errors["email"]}
          </div>
        </div>

        <div className={errors.phone ? "field-error" : ""}>
          <label htmlFor="phone">Phone</label>
          <InputMask
            id="phone"
            mask="(999) 999-9999"
            value={phone}
            onChange={(e) => handleInputChange("phone", e.target.value)}
            onKeyDown={(e) => handleKeyPress(e, phoneRef)}
            tabIndex={103}
            className="ant-input css-dev-only-do-not-override-1mmediq ant-input-outlined"
          ></InputMask>
          <div style={{ color: "#af6465", marginTop: "5px" }}>
            {errors["phone"]}
          </div>
        </div>

        <div>
          <Checkbox
            checked={hasAccess}
            onChange={(e) => handleInputChange("hasAccess", e.target.checked)}
            tabIndex={104}
          >
            Enable Login
          </Checkbox>
        </div>
      </div>
    </div>
  );
};

export default BasicDetailsComponent;
